import { useState, FormEvent, useCallback } from "react";

interface InterestFormProps {
  formId?: string;
  isFloating?: boolean;
  onClose?: () => void;
}

export default function InterestForm({
  formId = "interest-form",
  isFloating = false,
  onClose,
}: InterestFormProps) {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (status === "submitting") return;

      const form = e.currentTarget;
      const formData = new FormData(form);

      // Honeypot check
      const honeypot = formData.get("phone_alt");
      if (honeypot && String(honeypot).trim() !== "") {
        alert("상담 신청이 성공적으로 접수되었습니다!");
        form.reset();
        return;
      }

      setStatus("submitting");
      setErrorMsg("");

      const payload = {
        name: String(formData.get("name") || ""),
        phone: String(formData.get("phone") || ""),
        type: String(formData.get("date") || ""),
        message: String(formData.get("memo") || ""),
      };

      try {
        const response = await fetch(
          "https://script.google.com/macros/s/AKfycbw0LMb4G_UjHZTNdAhflm_fDxymjH4ISmadtqeCUjJ7Ffw8HqoMmM44a6oImQzY_j52/exec",
          {
            method: "POST",
            headers: {
              "Content-Type": "text/plain;charset=utf-8",
            },
            body: JSON.stringify(payload),
          }
        );

        if (response.ok) {
          alert("상담 신청이 성공적으로 접수되었습니다!");
          form.reset();
          setStatus("idle");
          if (onClose) {
            setTimeout(() => onClose(), 1500);
          }
        } else {
          setStatus("error");
          setErrorMsg("제출 중 오류가 발생했습니다. 다시 시도해주세요.");
        }
      } catch {
        setStatus("error");
        setErrorMsg("네트워크 오류가 발생했습니다. 다시 시도해주세요.");
      }
    },
    [status, onClose]
  );

  const inputBase =
    "w-full px-4 py-3 bg-white border border-background-200 rounded-md text-foreground-900 placeholder-foreground-400 text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors";

  const textareaBase =
    "w-full px-4 py-3 bg-white border border-background-200 rounded-md text-foreground-900 placeholder-foreground-400 text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors resize-none";

  return (
    <div className={isFloating ? "" : "w-full bg-background-100 py-12 md:py-16"}>
      <div className={isFloating ? "" : "max-w-xl mx-auto px-4 md:px-6"}>
        {!isFloating && (
          <h2 className="text-2xl md:text-3xl font-bold text-center text-foreground-900 mb-8 tracking-tight">
            관심고객 등록
          </h2>
        )}

        {status === "success" && (
          <div className="mb-4 p-4 bg-primary-50 border border-primary-200 rounded-md text-center">
            <span className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
              <i className="ri-check-line text-primary-600 text-2xl"></i>
            </span>
            <p className="text-primary-700 font-medium">
              등록신청이 완료되었습니다.
            </p>
            <p className="text-primary-600 text-sm mt-1">
              빠른 시일 내에 담당자가 연락드리겠습니다.
            </p>
          </div>
        )}

        {status === "error" && errorMsg && (
          <div className="mb-4 p-4 bg-accent-50 border border-accent-200 rounded-md text-center">
            <p className="text-accent-700 font-medium">{errorMsg}</p>
          </div>
        )}

        <form
          id={formId}
          data-readdy-form
          onSubmit={handleSubmit}
          className="space-y-3"
        >
          {/* Honeypot */}
          <input
            type="text"
            name="phone_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />

          <div>
            <input
              type="text"
              name="name"
              placeholder="성함"
              required
              className={inputBase}
            />
          </div>

          <div>
            <input
              type="tel"
              name="phone"
              placeholder="연락처('-' 없이 숫자만 입력)"
              required
              pattern="[0-9]*"
              inputMode="numeric"
              className={inputBase}
            />
          </div>

          <div>
            <input
              type="date"
              name="date"
              min={new Date().toISOString().split("T")[0]}
              required
              className={inputBase}
            />
          </div>

          <div>
            <textarea
              name="memo"
              placeholder="문의 내용(선택)"
              rows={3}
              maxLength={500}
              className={textareaBase}
            />
          </div>

          {/* Privacy Agreement */}
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              name="agree"
              value="1"
              required
              id={`agree-${formId}`}
              className="mt-1 w-4 h-4 accent-primary-600 cursor-pointer"
            />
            <label
              htmlFor={`agree-${formId}`}
              className="text-xs md:text-sm text-foreground-700 leading-relaxed cursor-pointer"
            >
              개인정보 수집이용에 동의합니다.{" "}
              <button
                type="button"
                onClick={() => setPrivacyOpen(true)}
                className="text-primary-600 underline hover:text-primary-700 cursor-pointer"
              >
                [자세히]
              </button>
            </label>
          </div>

          <button
            type="submit"
            disabled={status === "submitting"}
            className="w-full py-4 bg-primary-500 text-white font-semibold text-base rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {status === "submitting" ? "접수 중..." : isFloating ? "등록신청접수" : "관심고객 등록하기"}
          </button>
        </form>
      </div>

      {/* Privacy Modal */}
      {privacyOpen && (
        <div
          className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center px-4"
          onClick={() => setPrivacyOpen(false)}
        >
          <div
            className="bg-white rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-foreground-900">
                개인정보 수집·이용 동의
              </h3>
              <button
                onClick={() => setPrivacyOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-foreground-700 cursor-pointer"
              >
                <span className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-close-line text-xl"></i>
                </span>
              </button>
            </div>
            <div className="text-sm text-foreground-700 leading-relaxed space-y-3">
              <p>
                <strong>[필수] 개인정보 수집·이용 동의</strong>
              </p>
              <p>
                <strong>1. 개인정보수집주체</strong>
                <br />
                오베르원
              </p>
              <p>
                <strong>2. 수집 목적 및 항목</strong>
                <br />
                ① 수집목적 : 분양 관련 홍보 및 분양정보 안내
                <br />
                ② 수집항목 : 성명, 전화번호, 주소
              </p>
              <p>
                <strong>3. 개인정보의 보유 및 이용기간</strong>
                <br />
                당사는 개인정보 수집 및 이용 목적이 달성된 후에는 지체 없이
                파기합니다.
                <br />
                (분양 완료 시까지 보유)
              </p>
              <p>
                <strong>4. 개인정보 처리 위탁</strong>
                <br />
                ① 수탁자 : 오베르원
                <br />
                ② 위탁업무 : 홈페이지 제작·운영, 고객 DB 관리, 이벤트 운영 및
                성과 관리 대행
              </p>
              <p>
                <strong>5. 거부할 권리</strong>
                <br />
                귀하는 개인정보 수집 및 이용에 대한 동의를 거부할 수 있습니다.
                <br />
                단, 이 경우 분양정보 안내 서비스 제공이 불가합니다.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}